import { useEffect, useMemo } from "react"
import { useStore } from "zustand"

import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"

import { CandidatePoolResultsCard } from "@/components/guild-war/candidate-pool-results-card"
import { useCandidatePoolRouteStore } from "@/lib/stores/candidate-pool-ui-store"
import { applyCandidatePoolClientFilters } from "@/lib/utils/candidate-pool-utils"

const PAGE_SIZE = 25

export function CandidatePoolResultsSection({
  rawItems,
  hasData,
  isLoading,
  isError,
  onRefetch,
}: {
  rawItems: GuildWarCandidatePoolMember[]
  hasData: boolean
  isLoading: boolean
  isError: boolean
  onRefetch: () => void
}) {
  const store = useCandidatePoolRouteStore()
  const filters = useStore(store, (s) => s.filters)
  const page = useStore(store, (s) => s.page)
  const debouncedCandidateSearch = useStore(
    store,
    (s) => s.debouncedCandidateSearch,
  )

  const filteredCandidateItems = useMemo(
    () =>
      applyCandidatePoolClientFilters(rawItems, {
        day: filters.day,
        source: filters.source,
        paths: filters.paths,
        secondaryPath: filters.secondaryPath,
        absenceState: filters.absenceState,
        search: debouncedCandidateSearch,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      }),
    [rawItems, filters, debouncedCandidateSearch],
  )

  const totalCandidates = filteredCandidateItems.length
  const totalPages = Math.max(1, Math.ceil(totalCandidates / PAGE_SIZE))
  const candidateItems = useMemo(() => {
    const offset = (page - 1) * PAGE_SIZE
    return filteredCandidateItems.slice(offset, offset + PAGE_SIZE)
  }, [filteredCandidateItems, page])

  useEffect(() => {
    if (page > totalPages) {
      store.setState({ page: totalPages })
    }
  }, [page, totalPages, store])

  return (
    <CandidatePoolResultsCard
      hasData={hasData}
      isLoading={isLoading}
      isError={isError}
      totalCandidates={totalCandidates}
      candidateItems={candidateItems}
      page={page}
      totalPages={totalPages}
      onPrevPage={() => store.setState({ page: Math.max(1, page - 1) })}
      onNextPage={() =>
        store.setState({ page: Math.min(totalPages, page + 1) })
      }
      onRefetch={onRefetch}
    />
  )
}
